'use client';

import { useParams } from 'next/navigation';
import React, { useState } from 'react';

import { buildStrapiStopHours } from '@/app/builders';
import { doTranslation } from '@/app/i18n/client';
import { SearchType } from '@/app/types';
import BusStationAmenities from '@/components/bus-station/bus-station-amenities';
import styles from '@/components/bus-station/bus-station-detail.module.scss';
import BusStationHours from '@/components/bus-station/bus-station-hours';
import BusStationPaymentIcon from '@/components/bus-station/bus-station-payment-icon';
import BusStationMap from '@/components/bus-station/bus-station-map';
import AlertBrand from '@/components/global/alerts/alert-brand';
import AlertPrimary from '@/components/global/alerts/alert-primary';
import Search from '@/components/global/search/search';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  stopAndCityData: any;
  cityName: string;
}

const BusStationDetail = (props: Props) => {
  const { lng } = useParams() as { lng: string };
  const { t } = doTranslation(lng);
  const { stopAndCityData, cityName } = props;
  const [swap, setSwap] = useState<boolean>(false);
  const days = buildStrapiStopHours(
    stopAndCityData?.attributes.Hours ? stopAndCityData?.attributes.Hours : stopAndCityData?.attributes.location_hours
  );
  const stopSlug = stopAndCityData.attributes.stop_slug?.split('-');
  const stateCode =
    stopAndCityData.attributes.cities?.data[0].attributes.state?.data.attributes.state_id ||
    stopSlug[stopSlug.length - 1].toUpperCase();

  const buildAnnoucementText = (phoneNumber: string) => {
    const text = t(
      'Customers MUST call {{phoneNumberOrTheCarrier}} at least 24 hours in advance to ensure a stop at this location. Please Note that purchasing an E-Ticket does NOT guarantee the bus will stop.',
      {
        phoneNumberOrTheCarrier: phoneNumber ?? t('the carrier')
      }
    );
    if (phoneNumber) {
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: text.replace(phoneNumber, `<a href="tel:${phoneNumber}">${phoneNumber}</a>`)
          }}
        />
      );
    }
    return text;
  };

  const buildMaptitle = () => {
    const stopAndCityData = props.stopAndCityData.attributes;
    const city = stopAndCityData?.cities?.data[0]?.attributes?.city_name;
    const stop = stopAndCityData?.stop_name;
    const state = stopAndCityData?.cities?.data[0]?.attributes?.state?.data?.attributes?.state_id;
    if (stop === city) {
      return t('Map showing {{- city }}, {{ state }}', {
        city,
        state
      });
    }
    return t('Map showing {{- stop }} in {{- city }}, {{ state }}', {
      city,
      stop,
      state
    });
  };

  return (
    <div className={styles['station-info']}>
      <div className={styles.station}>
        <div className={styles.left}>
          <div className={styles.annoucements}>
            {stopAndCityData?.attributes.stop_advanced_notice && (
              <AlertBrand
                id="advance-notice-required"
                title={t('Advance Notice Required')}
                text={buildAnnoucementText(stopAndCityData?.attributes.phone_primary) as string}
              ></AlertBrand>
            )}
            {stopAndCityData.attributes.stop_special_announcements ? (
              <AlertPrimary
                title={t('Special Announcements')}
                text={t(stopAndCityData.attributes.stop_special_announcements)}
              ></AlertPrimary>
            ) : null}
            {stopAndCityData?.attributes.stop_boarding_instructions ? (
              <AlertPrimary
                title={t('Boarding Instructions')}
                text={t(stopAndCityData?.attributes.stop_boarding_instructions)}
              ></AlertPrimary>
            ) : null}
          </div>
          {stopAndCityData?.attributes.stop_show_open_24x7 ? (
            <BusStationHours
              hours={true}
              heading={
                stopAndCityData.attributes.stop_type === 'Bus Station'
                  ? t('{{ cityName }}, {{ stateCode }} Bus Station Hours', {
                      cityName: cityName,
                      stateCode: stateCode
                    })
                  : t('{{ cityName }}, {{ stateCode }} Bus Stop Hours', {
                      cityName: cityName,
                      stateCode: stateCode
                    })
              }
            />
          ) : (
            <BusStationHours
              days={days}
              hours={false}
              heading={
                stopAndCityData.attributes.stop_type === 'Bus Station'
                  ? t('{{ cityName }}, {{ stateCode }} Bus Station Hours', {
                      cityName: cityName,
                      stateCode: stateCode
                    })
                  : t('{{ cityName }}, {{ stateCode }} Bus Stop Hours', {
                      cityName: cityName,
                      stateCode: stateCode
                    })
              }
              stateCode={stateCode}
              stationName={stopAndCityData.attributes.stop_name}
            />
          )}
          {stopAndCityData?.attributes.payment_forms?.data.length ? (
            <BusStationPaymentIcon paymentForms={stopAndCityData?.attributes.payment_forms?.data} />
          ) : null}
          {stopAndCityData?.attributes.amenities?.data.length ? (
            <BusStationAmenities
              stopAndCityData={stopAndCityData}
              heading={
                stopAndCityData.attributes.stop_type === 'Bus Station'
                  ? t('Bus Station Amenities')
                  : t('Nearby Amenities')
              }
            />
          ) : null}
        </div>
        <BusStationMap
          markerIndex={null}
          details={true}
          state={
            props.stopAndCityData?.attributes?.cities?.data[0]?.attributes?.state?.data?.attributes?.state_id || null
          }
          stop={props.stopAndCityData.attributes?.stop_name}
          stops={[props.stopAndCityData]}
          title={buildMaptitle()}
        />
      </div>
      {stopAndCityData.attributes.cities && stopAndCityData.attributes.cities.data.length > 0 && (
        <div className={styles['booking-trip']}>
          <div className={styles.text}>
            {t(`Plan your next journey with Trailways {{ swap }}`, { swap: swap ? t('to') : t('from') })}&nbsp;
            <div className={styles.bold}>{stopAndCityData?.attributes.stop_name}</div>
          </div>
          <div className={styles['search-form']}>
            <Search
              type={SearchType.ONE_WAY}
              origin={stopAndCityData.attributes.cities.data[0].attributes.city_slug ?? ''}
              destination={''}
              setSwap={() => setSwap(!swap)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BusStationDetail;
